import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { showError } from '@feathr/components';
import { useStore } from '@feathr/extender/state';

import { useFundraisingEvents } from '../../hooks';

export interface IFundraisingSummaryMetrics {
  donationTotal: number;
  growthCredits: number;
  isLoading: boolean;
  hasError: boolean;
}

/**
 * Aggregates summary metrics for fundraising activity.
 *
 * On mount, fetches authoritative totals from `GET /donations/stats`
 * (computed live from breadcrumbs on the backend). When new donation SSE
 * events arrive, increments the local totals optimistically so the UI
 * stays in sync with the transaction table without a refetch round-trip.
 *
 * @returns Aggregated donation total, growth credits, loading and error state.
 */
function useFundraisingSummaryMetrics(): IFundraisingSummaryMetrics {
  const { t } = useTranslation();
  const { Breadcrumbs } = useStore();
  const [donationTotal, setDonationTotal] = useState(0);
  const [growthCredits, setGrowthCredits] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    let cancelled = false;

    Breadcrumbs.getDonationStats()
      .then((stats) => {
        if (cancelled) {
          return;
        }
        setDonationTotal(stats.total_donation_volume);
        setGrowthCredits(stats.total_growth_credits);
        setIsLoading(false);
      })
      .catch((error: unknown) => {
        if (cancelled) {
          return;
        }
        setHasError(true);
        setIsLoading(false);
        showError(error, t);
      });

    return (): void => {
      cancelled = true;
    };
  }, [Breadcrumbs, t]);

  useFundraisingEvents({
    onDonationCreated: (event) => {
      setDonationTotal((prev) => prev + event.amount);
      setGrowthCredits((prev) => prev + event.credits_earned);
    },
  });

  return {
    donationTotal,
    growthCredits,
    isLoading,
    hasError,
  };
}

export default useFundraisingSummaryMetrics;
